import * as Sentry from "@sentry/react";
import { Switch, Route, Redirect } from "wouter";
import { queryClient } from "./lib/query-client";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { FirebaseProvider } from "@/components/layout/firebase-provider";
import { BrandingProvider } from "@/hooks/use-branding";
import { ThemeProvider } from "@/components/theme-provider";
import { useAuth } from "@/hooks/use-auth";
import { useMfaStatus } from "@/hooks/use-mfa";
import { resolveAuthedScreen } from "@/lib/app-gate";
import { TwilioDeviceProvider } from "@/components/calls/twilio-device-provider";
import { ActiveCallWidget } from "@/components/calls/active-call-widget";
import { lazy, Suspense, useEffect, type ComponentType } from "react";

// Critical path — loaded eagerly
import NotFound from "@/pages/not-found";
import Landing from "@/pages/landing";
import Dashboard from "@/pages/dashboard";
import { PermissionGuard } from "@/components/permission-guard";
import { AcceptFirmInvite } from "@/components/onboarding/accept-firm-invite";
import { DOJO_PATH, LEGACY_TRAINING_LAB_PATH, LegacyTrainingLabRedirect } from "@/components/agent-training-lab/routes";
import { AGENTS_REVIEW_PATH, AGENTS_TEST_DRIVE_PATH } from "@/components/agents-qa/use-agent-qa";

/**
 * Wraps React.lazy to handle stale chunk errors after a deployment.
 *
 * After a deploy, old chunk filenames (e.g. claims-abc123.js) no longer exist.
 * When the browser tries to fetch them, the request either 404s or returns HTML.
 * This wrapper detects those failures and reloads the page once so the browser
 * fetches the fresh index.html with correct chunk hashes.
 *
 * Uses sessionStorage to prevent infinite reload loops.
 */
function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(() =>
    importFn().catch((error: Error) => {
      const isChunkError =
        error.message.includes('Failed to fetch dynamically imported module') ||
        error.message.includes('Loading chunk') ||
        error.message.includes('Importing a module script failed') ||
        error.message.includes('error loading dynamically imported module');

      if (!isChunkError) throw error;

      // Only retry once per session to prevent infinite reload loops
      const reloadKey = 'chunk-reload-retry';
      const hasRetried = sessionStorage.getItem(reloadKey);

      if (!hasRetried) {
        sessionStorage.setItem(reloadKey, '1');
        console.warn('[App] Stale chunk detected after deployment — reloading page');
        window.location.reload();
        // Return a never-resolving promise while the page reloads
        return new Promise<{ default: T }>(() => {});
      }

      // Already retried once — clear flag and let the error propagate
      sessionStorage.removeItem(reloadKey);
      throw error;
    }),
  );
}

// Email composer popup route — loaded eagerly so it renders quickly inside the popup window
import { EmailComposerPopupPage } from "@/pages/email-composer-popup";
// Contact form popup route — loaded eagerly so it renders quickly inside the popup window
import { ContactFormPopupPage } from "@/pages/contact-form-popup";
// Edit claim popup route — loaded eagerly so it renders quickly inside the popup window
import { EditClaimPopupPage } from "@/pages/edit-claim-popup";
// Lead popup route — loaded eagerly so it renders quickly inside the popup window
import { LeadPopupPage } from "@/pages/lead-popup";
// Chat popup route — loaded eagerly so it renders quickly inside the popup window
import { ChatPopupPage } from "@/pages/chat-popup";

// Route-level code splitting — loaded on demand (with stale-chunk retry)
const Claims = lazyWithRetry(() => import("@/pages/claims"));
const ClaimDetail = lazyWithRetry(() => import("@/pages/claim-detail").then(m => ({ default: m.ClaimDetail })));
const ClaimPostalJobs = lazyWithRetry(() => import("@/pages/claim-postal-jobs"));
const Leads = lazyWithRetry(() => import("@/pages/leads"));
const LeadDetail = lazyWithRetry(() => import("@/pages/lead-detail"));
const Gmail = lazyWithRetry(() => import("@/pages/gmail"));
const Contacts = lazyWithRetry(() => import("@/pages/contacts"));
const FlaggedContacts = lazyWithRetry(() => import("@/pages/flagged-contacts"));
const ClaimsMap = lazyWithRetry(() => import("@/pages/claims-map"));
const Documents = lazyWithRetry(() => import("@/pages/documents"));
const AIDrafting = lazyWithRetry(() => import("@/pages/ai-drafting"));
const Reports = lazyWithRetry(() => import("@/pages/reports"));
const Commissions = lazyWithRetry(() => import("@/pages/commissions"));
const Apps = lazyWithRetry(() => import("@/pages/apps"));
const VoiceRecorder = lazyWithRetry(() => import("@/pages/voice-recorder"));
const NLPSearch = lazyWithRetry(() => import("@/pages/nlp-search"));
const Tasks = lazyWithRetry(() => import("@/pages/tasks"));
const DamageScan = lazyWithRetry(() => import("@/pages/damage-scan"));
const Settings = lazyWithRetry(() => import("@/pages/settings"));
const QuestionnairePage = lazyWithRetry(() => import("@/pages/questionnaire"));
const QuestionnairePreviewPage = lazyWithRetry(() => import("@/pages/questionnaire").then(m => ({ default: m.QuestionnairePreviewPage })));
const OnboardingPage = lazyWithRetry(() => import("@/pages/onboarding"));
const MfaGatePage = lazyWithRetry(() => import("@/pages/mfa-gate"));
const FirmOnboardingPage = lazyWithRetry(() => import("@/pages/firm-onboarding"));
const CardPendingPage = lazyWithRetry(() => import("@/pages/card-pending"));
const FirmAccountingPage = lazyWithRetry(() => import("@/pages/firm-accounting"));
const TrainingLibrary = lazyWithRetry(() => import("@/pages/training-library"));
const DamageTraining = lazyWithRetry(() => import("@/pages/damage-training"));
const ChatPage = lazyWithRetry(() => import("@/pages/chat"));
const AgentChatPage = lazyWithRetry(() => import("@/pages/agent-chat"));
const ActivityTracking = lazyWithRetry(() => import("@/pages/activity-tracking"));
const AIAgents = lazyWithRetry(() => import("@/pages/ai-agents"));
const KnowledgeBase = lazyWithRetry(() => import("@/pages/knowledge-base"));
const ProfilePage = lazyWithRetry(() => import("@/pages/profile"));
const AICorrections = lazyWithRetry(() => import("@/pages/ai-corrections"));
const Dojo = lazyWithRetry(() => import("@/pages/agent-training-lab"));
const AgentsTestDrive = lazyWithRetry(() => import("@/pages/agents-test-drive"));
const AgentsReview = lazyWithRetry(() => import("@/pages/agents-review"));
const QuickBooksSettings = lazyWithRetry(() => import("@/pages/quickbooks-settings"));
const SharedPhotoPage = lazyWithRetry(() => import("@/pages/shared-photo"));
const PrivacyPolicyPage = lazyWithRetry(() => import("@/pages/privacy-policy"));
const Imports = lazyWithRetry(() => import("@/pages/imports"));
const ImportDetail = lazyWithRetry(() => import("@/pages/import-detail"));
const ClaimImport = lazyWithRetry(() => import("@/pages/claim-import"));
const ExportJobs = lazyWithRetry(() => import("@/pages/export-jobs"));
const CfoHub = lazyWithRetry(() => import("@/pages/cfo-hub"));
const CfoUserDetail = lazyWithRetry(() => import("@/pages/cfo-user-detail"));
const CfoSettlementDetail = lazyWithRetry(() => import("@/pages/cfo-settlement-detail"));
const TimesheetsAdmin = lazyWithRetry(() => import("@/pages/timesheets-admin"));
const CarrierDeadlineMatrixPage = lazyWithRetry(
  () => import("@/pages/carrier-deadline-matrix")
);
const AdminEmailData = lazyWithRetry(() => import("@/pages/admin-email-data"));
const Carriers = lazyWithRetry(() => import("@/pages/carriers"));
const CarrierDetail = lazyWithRetry(() => import("@/pages/carrier-detail"));
const AdminBrokenFiles = lazyWithRetry(() => import("@/pages/admin-broken-files"));
const BodyImportCleanup = lazyWithRetry(() => import("@/pages/body-import-cleanup"));
const SuperadminOrganizations = lazyWithRetry(() => import("@/pages/superadmin-organizations"));
const SuperadminOrgDetail = lazyWithRetry(() => import("@/pages/superadmin-org-detail"));
const SuperadminBeta = lazyWithRetry(() => import("@/pages/superadmin-beta"));
const OrgSelector = lazyWithRetry(() => import("@/pages/org-selector"));
const FeedbackBoard = lazyWithRetry(() => import("@/pages/feedback"));
const CheckProcessingDesk = lazyWithRetry(() => import("@/pages/check-processing-desk"));

function PageLoader() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', gap: '0.75rem', fontFamily: 'system-ui, sans-serif',
    }}>
      <div style={{
        width: 24, height: 24, border: '3px solid #e5e7eb',
        borderTopColor: '#2563eb', borderRadius: '50%',
        animation: 'spin 0.6s linear infinite',
      }} />
      <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading...</span>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

function Router() {
  const { user, isAuthenticated, isLoading, orgDeactivated } = useAuth();

  // Check if authenticated user needs to complete onboarding
  const needsOnboarding = isAuthenticated && user && !user.profileCompleted;
  const needsFirmOnboarding = isAuthenticated && user && (user as any).needsFirmOnboarding;
  const firmLocked = isAuthenticated && user?.firmBilling?.locked;

  // Check if authenticated multi-org user still needs to pick an org
  const needsOrgSelection =
    isAuthenticated &&
    user &&
    Array.isArray(user?.orgs) &&
    user.orgs.length > 1 &&
    !user.activeOrgId;

  // MIS-265: a signed-in person in a firm sees the MFA screen instead of the app until the server says this session
  // is cleared. The server blocks every other route either way; this is only which screen to show.
  const hasFirm = isAuthenticated && !!user?.membership?.orgId && !needsOrgSelection;
  const { data: mfaStatus, isLoading: mfaLoading } = useMfaStatus(hasFirm);
  const mfaBlocked = hasFirm && !!mfaStatus && mfaStatus.state !== "ok";

  const authedScreen = resolveAuthedScreen({
    needsOrgSelection: !!needsOrgSelection,
    mfaBlocked,
    needsFirmOnboarding: !!needsFirmOnboarding,
    cardPendingForMember: !!user?.firmBilling && user.firmBilling.cardGate !== "open" && !user.isSuperAdmin,
    needsProfileOnboarding: !!needsOnboarding,
    firmLocked: !!firmLocked,
  });

  // Show nothing while loading authentication
  // Once loading is done, user will be either a User object or null (not undefined)
  if (isLoading || (hasFirm && mfaLoading)) {
    return null;
  }

  // Questionnaire route is public and should work for everyone
  const questionnaireRoute = <Route path="/questionnaire/:token" component={QuestionnairePage} />;
  const questionnairePreviewRoute = <Route path="/questionnaire/preview/:templateId" component={QuestionnairePreviewPage} />;

  return (
    <Suspense fallback={<PageLoader />}>
      {isAuthenticated && <AcceptFirmInvite />}
      <Switch>
        {/* Email composer popup — accessible without auth (session cookie is shared same-origin) */}
        <Route path="/email-composer" component={EmailComposerPopupPage} />
        {/* Contact form popup — accessible without auth (session cookie is shared same-origin) */}
        <Route path="/contact-form" component={ContactFormPopupPage} />
        {/* Edit claim popup — accessible without auth (session cookie is shared same-origin) */}
        <Route path="/edit-claim" component={EditClaimPopupPage} />
        {/* Lead popup — accessible without auth (session cookie is shared same-origin) */}
        <Route path="/lead-popup/:id" component={LeadPopupPage} />
        {/* Chat popup — accessible without auth (session cookie is shared same-origin) */}
        <Route path="/chat-popup" component={ChatPopupPage} />
        {questionnairePreviewRoute}
        {questionnaireRoute}
        <Route path="/shared/photo/:token" component={SharedPhotoPage} />
        <Route path="/privacy-policy" component={PrivacyPolicyPage} />
        {orgDeactivated ? (
          // Active firm deactivated mid-session: the session is still valid, so
          // send the user to the picker (switch to another firm / sign out)
          // instead of the logged-out Landing page. Checked before the
          // !isAuthenticated branch, which this state would otherwise fall into.
          <Route path="*" component={OrgSelector} />
        ) : !isAuthenticated ? (
          <>
            <Route path="/" component={Landing} />
            <Route path="/login" component={Landing} />
            <Route path="*" component={Landing} />
          </>
        ) : authedScreen === "org_selection" ? (
          <Route path="*" component={OrgSelector} />
        ) : authedScreen === "mfa" && mfaStatus ? (
          <Route path="*">
            <MfaGatePage status={mfaStatus} />
          </Route>
        ) : authedScreen === "firm_onboarding" ? (
          <>
            <Route path="/onboarding/firm" component={FirmOnboardingPage} />
            <Route path="*" component={FirmOnboardingPage} />
          </>
        ) : authedScreen === "card_pending_member" ? (
          <Route path="*" component={CardPendingPage} />
        ) : authedScreen === "profile_onboarding" ? (
          <Route path="*" component={OnboardingPage} />
        ) : authedScreen === "locked" ? (
          <>
            <Route path="/settings" component={Settings} />
            <Route path="/onboarding/firm" component={FirmOnboardingPage} />
            <Route path="*" component={Settings} />
          </>
        ) : (
          <>
            <Route path="/" component={Dashboard} />
            <Route path="/dashboard" component={Dashboard} />
            <Route path="/login"><Redirect to="/" /></Route>
            <Route path="/claims">
              <PermissionGuard permissionKey="claims.read_assigned"><Claims /></PermissionGuard>
            </Route>
            <Route path="/claims/:claimId/checks/:checkId/process">
              <PermissionGuard permissionKey="finances.manage_carrier_checks"><CheckProcessingDesk /></PermissionGuard>
            </Route>
            <Route path="/claims/:claimId/postal/jobs">
              <PermissionGuard permissionKey="claims.read_assigned"><ClaimPostalJobs /></PermissionGuard>
            </Route>
            <Route path="/claims/:claimId">
              <PermissionGuard permissionKey="claims.read_assigned"><ClaimDetail /></PermissionGuard>
            </Route>
            <Route path="/leads">
              <PermissionGuard permissionKey="leads.read"><Leads /></PermissionGuard>
            </Route>
            <Route path="/leads/:id">
              <PermissionGuard permissionKey="leads.read"><LeadDetail /></PermissionGuard>
            </Route>
            <Route path="/email">
              <PermissionGuard permissionKey="communications.read_assigned"><Gmail /></PermissionGuard>
            </Route>
            <Route path="/gmail">
              <PermissionGuard permissionKey="communications.read_assigned"><Gmail /></PermissionGuard>
            </Route>
            <Route path="/contacts/flagged">
              <PermissionGuard permissionKey="contacts.read"><FlaggedContacts /></PermissionGuard>
            </Route>
            <Route path="/contacts">
              <PermissionGuard permissionKey="contacts.read"><Contacts /></PermissionGuard>
            </Route>
            <Route path="/carriers/:id">
              <PermissionGuard permissionKey="contacts.read"><CarrierDetail /></PermissionGuard>
            </Route>
            <Route path="/carriers">
              <PermissionGuard permissionKey="contacts.read"><Carriers /></PermissionGuard>
            </Route>
            <Route path="/claims-map">
              <PermissionGuard permissionKey="claims.read_assigned"><ClaimsMap /></PermissionGuard>
            </Route>
            <Route path="/documents">
              <PermissionGuard permissionKey="documents.read_assigned"><Documents /></PermissionGuard>
            </Route>
            <Route path="/apps">
              <PermissionGuard permissionKey="ai.use_drafting"><Apps /></PermissionGuard>
            </Route>
            <Route path="/apps/ai-drafting">
              <PermissionGuard permissionKey="ai.use_drafting"><AIDrafting /></PermissionGuard>
            </Route>
            <Route path="/apps/voice-recorder">
              <PermissionGuard permissionKey="ai.use_drafting"><VoiceRecorder /></PermissionGuard>
            </Route>
            <Route path="/apps/nlp-search">
              <PermissionGuard permissionKey="search.execute"><NLPSearch /></PermissionGuard>
            </Route>
            <Route path="/apps/damage-scan">
              <PermissionGuard permissionKey="ai.use_drafting"><DamageScan /></PermissionGuard>
            </Route>
            <Route path="/apps/ai-agents">
              <PermissionGuard permissionKey="ai.use_drafting"><AIAgents /></PermissionGuard>
            </Route>
            <Route path="/apps/knowledge-base">
              <PermissionGuard permissionKey="ai.view_training_data"><KnowledgeBase /></PermissionGuard>
            </Route>
            <Route path="/ai-drafting">
              <PermissionGuard permissionKey="ai.use_drafting"><AIDrafting /></PermissionGuard>
            </Route>
            <Route path="/reports">
              <PermissionGuard permissionKey="reports.view_operational"><Reports /></PermissionGuard>
            </Route>
            <Route path="/training-library">
              <PermissionGuard permissionKey="ai.view_training_data"><TrainingLibrary /></PermissionGuard>
            </Route>
            <Route path="/damage-training">
              <PermissionGuard permissionKey="ai.manage_training_data"><DamageTraining /></PermissionGuard>
            </Route>
            <Route path="/commissions">
              <PermissionGuard permissionKey="finances.view_claim_commission_all"><Commissions /></PermissionGuard>
            </Route>
            <Route path="/tasks">
              <PermissionGuard permissionKey="claims.read_assigned"><Tasks /></PermissionGuard>
            </Route>
            <Route path="/activity-tracking">
              <PermissionGuard permissionKey="users.view_directory"><ActivityTracking /></PermissionGuard>
            </Route>
            <Route path="/chat/agent/:spaceId" component={AgentChatPage} />
            <Route path="/chat/agent" component={AgentChatPage} />
            <Route path="/chat" component={ChatPage} />
            <Route path="/quickbooks">
              <PermissionGuard permissionKey="settings.manage_integrations"><QuickBooksSettings /></PermissionGuard>
            </Route>
            <Route path="/settings">
              <Settings />
            </Route>
            <Route path="/accounting">
              <FirmAccountingPage />
            </Route>
            <Route path="/onboarding/firm" component={FirmOnboardingPage} />
            <Route path="/ai-corrections">
              <PermissionGuard permissionKey="ai.manage_training_data"><AICorrections /></PermissionGuard>
            </Route>
            <Route path={DOJO_PATH}>
              <PermissionGuard permissionKey="users.manage_roles"><Dojo /></PermissionGuard>
            </Route>
            <Route path={LEGACY_TRAINING_LAB_PATH} component={LegacyTrainingLabRedirect} />
            {/* QA only: the page goes back to the dashboard unless the Run agent status route answers. */}
            <Route path={AGENTS_TEST_DRIVE_PATH}>
              <PermissionGuard permissionKey="users.manage_roles"><AgentsTestDrive /></PermissionGuard>
            </Route>
            <Route path={AGENTS_REVIEW_PATH}>
              <PermissionGuard permissionKey="users.manage_roles"><AgentsReview /></PermissionGuard>
            </Route>
            <Route path="/cfo/settlement-detail">
              <PermissionGuard permissionKey="users.manage_roles"><CfoSettlementDetail /></PermissionGuard>
            </Route>
            <Route path="/cfo/user/:id">
              <PermissionGuard permissionKey="users.manage_roles"><CfoUserDetail /></PermissionGuard>
            </Route>
            <Route path="/cfo">
              <PermissionGuard permissionKey="users.manage_roles"><CfoHub /></PermissionGuard>
            </Route>
            <Route path="/timesheets">
              <TimesheetsAdmin />
            </Route>
            <Route path="/apps/imports/:jobId">
              <PermissionGuard permissionKey="settings.manage_imports"><ImportDetail /></PermissionGuard>
            </Route>
            <Route path="/apps/imports">
              <PermissionGuard permissionKey="settings.manage_imports"><Imports /></PermissionGuard>
            </Route>
            <Route path="/apps/claim-import">
              <PermissionGuard permissionKey="settings.manage_imports"><ClaimImport /></PermissionGuard>
            </Route>
            <Route path="/apps/exports">
              <PermissionGuard permissionKey="claims.read_assigned"><ExportJobs /></PermissionGuard>
            </Route>
            <Route path="/carrier-deadline-matrix">
              <PermissionGuard permissionKey="claims.read_assigned"><CarrierDeadlineMatrixPage /></PermissionGuard>
            </Route>
            <Route path="/apps/admin-email-data">
              <PermissionGuard permissionKey="communications.delete"><AdminEmailData /></PermissionGuard>
            </Route>
            <Route path="/apps/admin-broken-files">
              <PermissionGuard permissionKey="settings.manage_permissions"><AdminBrokenFiles /></PermissionGuard>
            </Route>
            <Route path="/apps/body-import-cleanup">
              <PermissionGuard permissionKey="communications.delete"><BodyImportCleanup /></PermissionGuard>
            </Route>
            <Route path="/profile" component={ProfilePage} />
            <Route path="/onboarding" component={OnboardingPage} />
            <Route path="/select-org" component={OrgSelector} />
            <Route path="/feedback" component={FeedbackBoard} />
            <Route path="/superadmin/organizations/:id">
              {user?.isSuperAdmin ? <SuperadminOrgDetail /> : <NotFound />}
            </Route>
            <Route path="/superadmin/organizations">
              {user?.isSuperAdmin ? <SuperadminOrganizations /> : <NotFound />}
            </Route>
            <Route path="/superadmin/beta">
              {user?.isSuperAdmin ? <SuperadminBeta /> : <NotFound />}
            </Route>
            <Route component={NotFound} />
          </>
        )}
      </Switch>
    </Suspense>
  );
}

function App() {
  // Check for Gmail OAuth callback IMMEDIATELY before anything else renders
  const params = new URLSearchParams(window.location.search);
  const gmailConnected = params.get('gmail_connected');

  // Check if this window was opened as the email composer popup
  if (window.location.pathname === '/email-composer' && window.opener) {
    return <EmailComposerPopupPage />;
  }

  // Check if this window was opened as the contact form popup
  if (window.location.pathname === '/contact-form' && window.opener) {
    return <ContactFormPopupPage />;
  }

  // Check if this window was opened as the edit claim popup
  if (window.location.pathname === '/edit-claim' && window.opener) {
    return <EditClaimPopupPage />;
  }

  // Check if this window was opened as the lead popup
  if (window.location.pathname.startsWith('/lead-popup/') && window.opener) {
    return <LeadPopupPage />;
  }

  // Check if this window was opened as the chat popup
  if (window.location.pathname === '/chat-popup' && window.opener) {
    return <ChatPopupPage />;
  }
  
  // If this is a popup window with Gmail connection result, close it immediately
  if (gmailConnected && window.opener) {
    console.log('[Gmail OAuth] Popup callback detected, closing popup...');
    // Close the popup and notify the opener
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({ type: 'gmail_connected', status: gmailConnected }, '*');
    }
    window.close();
    
    // Show a closing message in case window.close() is blocked
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <h2>Email Connected Successfully</h2>
        <p>This window will close automatically...</p>
        <button onClick={() => window.close()}>Close Window</button>
      </div>
    );
  }

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          height: '100vh', flexDirection: 'column', gap: '1rem',
          fontFamily: 'system-ui, sans-serif',
        }}>
          <h2>Something went wrong</h2>
          <p style={{ color: '#666', maxWidth: 480, textAlign: 'center' }}>
            An unexpected error occurred. The error has been reported automatically.
          </p>
          <button
            onClick={resetError}
            style={{
              padding: '0.5rem 1.5rem', borderRadius: '0.375rem',
              background: '#2563eb', color: '#fff', border: 'none', cursor: 'pointer',
            }}
          >
            Try Again
          </button>
        </div>
      )}
    >
      <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <BrandingProvider>
          <FirebaseProvider>
            <TooltipProvider>
              <TwilioDeviceProvider>
                <Toaster />
                <Router />
                <ActiveCallWidget />
              </TwilioDeviceProvider>
            </TooltipProvider>
          </FirebaseProvider>
        </BrandingProvider>
      </QueryClientProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
